/**
 * Batch #14.197 — Timeline réutilisable des notifications liées à une action.
 *
 * Affiche les étapes : 📧 envoyé → 👁 ouvert → 🔗 cliqué → ✅ action complétée
 * 1 ligne par destinataire (locataire 1, locataire 2, landlord, etc.)
 *
 * Usage :
 *   <NotificationTimeline relatedType="rent_increase" relatedId={wfId} />
 *   <NotificationTimeline relatedType="bail_tal" relatedId={workflowId} />
 *   <NotificationTimeline relatedType="chat" relatedId={questionId} compact />
 */
import { useEffect, useState } from "react";
import { api } from "@/api";
import {
  Mail, MailOpen, MousePointerClick, CheckCircle2, AlertTriangle,
  Loader2, Smartphone, Send, RefreshCw,
} from "lucide-react";

const STATUS_MAP = {
  sent:    { label: "Envoyée", icon: Mail, color: "text-blue-600", bg: "bg-blue-50 border-blue-200" },
  opened:  { label: "Ouverte", icon: MailOpen, color: "text-amber-600", bg: "bg-amber-50 border-amber-200" },
  clicked: { label: "Cliquée", icon: MousePointerClick, color: "text-violet-600", bg: "bg-violet-50 border-violet-200" },
  read:    { label: "Lue", icon: CheckCircle2, color: "text-cyan-600", bg: "bg-cyan-50 border-cyan-200" },
  received:{ label: "Reçue", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
  signed:  { label: "Signée", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
  reminded:{ label: "Relancée", icon: AlertTriangle, color: "text-orange-600", bg: "bg-orange-50 border-orange-200" },
  failed:  { label: "Échec", icon: AlertTriangle, color: "text-rose-600", bg: "bg-rose-50 border-rose-200" },
};

const fmtRelative = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diff < 1) return "à l'instant";
    if (diff < 60) return `il y a ${diff}m`;
    if (diff < 1440) return `il y a ${Math.floor(diff / 60)}h`;
    if (diff < 10080) return `il y a ${Math.floor(diff / 1440)}j`;
    return d.toLocaleDateString("fr-CA");
  } catch {
    return iso.slice(0, 16);
  }
};

export default function NotificationTimeline({ relatedType, relatedId, compact = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!relatedType || !relatedId) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/notifications/for/${relatedType}/${relatedId}`);
      setItems(r.data?.items || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [relatedType, relatedId]);

  if (loading) return <div className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin text-slate-400 mx-auto" /></div>;

  if (!items.length) {
    return <div className="text-xs text-slate-400 italic py-2">Aucune notification envoyée pour cette action.</div>;
  }

  return (
    <div className={compact ? "space-y-1" : "space-y-2"} data-testid="notif-timeline">
      <div className="flex items-center justify-between text-[11px]">
        <div className="text-slate-500 uppercase font-bold tracking-wider">Suivi des communications · {items.length}</div>
        <button onClick={load} className="text-slate-400 hover:text-slate-600 inline-flex items-center gap-0.5" data-testid="notif-timeline-refresh">
          <RefreshCw className="w-3 h-3" />
        </button>
      </div>
      {items.map((it) => <TimelineRow key={it.id} item={it} compact={compact} />)}
    </div>
  );
}

function TimelineRow({ item, compact }) {
  const conf = STATUS_MAP[item.status] || STATUS_MAP.sent;
  const Icon = conf.icon;
  const finalIcon = item.final_action_at ? CheckCircle2 : null;

  return (
    <div className={`flex items-start gap-2 border rounded ${conf.bg} ${compact ? "p-1.5" : "p-2.5"}`} data-testid={`notif-timeline-row-${item.id}`}>
      <div className={`shrink-0 ${conf.color} mt-0.5`}>
        {finalIcon ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap text-xs">
          <span className="font-semibold text-slate-800">{item.recipient_name || "—"}</span>
          {item.recipient_role && (
            <span className="text-[9px] uppercase text-slate-500 bg-white border border-slate-200 px-1 rounded">{item.recipient_role}</span>
          )}
          <span className={`text-[10px] font-bold uppercase ${conf.color}`}>{conf.label}</span>
        </div>
        <div className="text-[10px] text-slate-500 flex flex-wrap gap-x-2">
          {(item.channels_succeeded || []).map((c) => (
            <span key={c} className="inline-flex items-center gap-0.5">
              {c === "email" ? <Mail className="w-2.5 h-2.5" /> : c === "sms" ? <Smartphone className="w-2.5 h-2.5" /> : c === "push" ? <Send className="w-2.5 h-2.5" /> : null}
              {c}
            </span>
          ))}
          {item.sms_dlr_status && <span className="font-mono">DLR:{item.sms_dlr_status}</span>}
        </div>
        <div className="text-[10px] mt-0.5 space-y-0">
          {item.sent_at && <div>📤 Envoyée {fmtRelative(item.sent_at)}</div>}
          {item.email_opened_at && <div className="text-amber-700">👁 Ouverte {fmtRelative(item.email_opened_at)}</div>}
          {item.magic_link_clicked_at && <div className="text-violet-700">🔗 Lien cliqué {fmtRelative(item.magic_link_clicked_at)}</div>}
          {item.portal_visited_after && <div className="text-cyan-700">🚪 Portail visité après</div>}
          {item.final_action_at && <div className="text-emerald-700 font-bold">✅ Action complétée {fmtRelative(item.final_action_at)}</div>}
          {item.reminder_sent_at && <div className="text-orange-600">📤 Relancée {fmtRelative(item.reminder_sent_at)}</div>}
        </div>
      </div>
    </div>
  );
}

/**
 * Popover compact — affiche la timeline au survol d'un badge ou bouton.
 * Usage :
 *   <NotificationTimelinePopover trigger={<MyButton/>} relatedType="..." relatedId="..." />
 */
export function NotificationTimelinePopover({ trigger, relatedType, relatedId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      {trigger}
      {open && (
        <div className="absolute z-50 right-0 top-full mt-1 w-80 bg-white border border-slate-200 rounded-lg shadow-xl p-2 max-h-96 overflow-y-auto">
          <NotificationTimeline relatedType={relatedType} relatedId={relatedId} compact />
        </div>
      )}
    </div>
  );
}
